import { useCallback, useEffect, useState } from 'react'
import { PageHeader, btnPrimary, colors } from '../../components/ui'
import { api } from '../../services/api'

interface RateLimitState {
  readonly configured: number | null
  readonly effective: number
  readonly defaultCap: number
  readonly floor: number
  readonly ceiling: number
}

const smallBtnStyle: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  borderRadius: '4px',
  border: '1px solid #d1d5db',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.85rem',
}

export function RateLimitSettingsPage() {
  const [state, setState] = useState<RateLimitState | null>(null)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<RateLimitState>('/admin/rate-limit')
      if (res.data) {
        setState(res.data)
        setInput(String(res.data.configured ?? res.data.defaultCap))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const inputValue = Number(input)
  const inputValid =
    state != null && Number.isInteger(inputValue) && inputValue >= state.floor && inputValue <= state.ceiling

  const handleSave = async () => {
    if (!state || !inputValid) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await api.put<RateLimitState>('/admin/rate-limit', { maxPerMinute: inputValue })
      if (res.data) {
        setState((prev) => (prev ? { ...prev, ...res.data } : null))
        setSuccess(`Saved -- effective cap is now ${res.data.effective} req/min/IP.`)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!state) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await api.delete<RateLimitState>('/admin/rate-limit')
      if (res.data) {
        setState((prev) => (prev ? { ...prev, ...res.data } : null))
        setInput(String(res.data.effective))
        setSuccess(`Reset -- effective cap is now ${res.data.effective} req/min/IP (default).`)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageHeader title="API Rate Limit" subtitle="Loading..." />
  if (!state) {
    return (
      <div>
        <PageHeader title="API Rate Limit" subtitle="Failed to load." />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    )
  }

  const dirty = inputValue !== (state.configured ?? state.defaultCap)

  return (
    <div>
      <PageHeader
        title="API Rate Limit"
        subtitle="Per-IP request cap for the Central API. Tune while evaluating performance; changes take effect within ~15 seconds."
      />

      {error && <ErrorBanner>{error}</ErrorBanner>}
      {success && <SuccessBanner>{success}</SuccessBanner>}

      <div
        style={{
          padding: '1.25rem',
          background: colors.bgCard,
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          marginBottom: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <Row label="Effective now" value={`${state.effective} req/min/IP`} bold />
        <Row
          label="Configured override"
          value={state.configured == null ? 'none (using default)' : `${state.configured} req/min/IP`}
        />
        <Row label="Default" value={`${state.defaultCap} req/min/IP`} />
        <Row label="Allowed range" value={`${state.floor} -- ${state.ceiling}`} />
      </div>

      <div
        style={{
          padding: '1.25rem',
          background: colors.bgCard,
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
        }}
      >
        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
          New cap (requests per minute per IP)
        </label>
        <input
          type="number"
          value={input}
          onChange={(e) => {
            setInput(e.target.value)
            setSuccess(null)
          }}
          min={state.floor}
          max={state.ceiling}
          step={50}
          style={{
            width: '160px',
            padding: '0.4rem 0.6rem',
            fontSize: '0.95rem',
            border: `1px solid ${inputValid || input === '' ? '#d1d5db' : '#fca5a5'}`,
            borderRadius: '4px',
          }}
        />
        {!inputValid && input !== '' && (
          <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.4rem' }}>
            Must be an integer between {state.floor} and {state.ceiling}.
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <button
            style={{ ...smallBtnStyle, color: '#6b7280' }}
            onClick={handleReset}
            disabled={saving || state.configured == null}
            title={state.configured == null ? 'No override is currently set' : 'Clear the override and use the default'}
          >
            Reset to Default
          </button>
          <div style={{ flex: 1 }} />
          <button
            style={{ ...btnPrimary, opacity: !dirty || !inputValid || saving ? 0.5 : 1 }}
            onClick={handleSave}
            disabled={!dirty || !inputValid || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', fontSize: '0.875rem' }}>
      <span style={{ width: '180px', color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: bold ? 600 : 400, color: '#1f2937' }}>{value}</span>
    </div>
  )
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '0.75rem',
        background: '#fef2f2',
        border: '1px solid #fca5a5',
        borderRadius: '6px',
        color: '#dc2626',
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  )
}

function SuccessBanner({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '0.75rem',
        background: '#f0fdf4',
        border: '1px solid #86efac',
        borderRadius: '6px',
        color: '#16a34a',
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  )
}
